import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getDatabasesByCompanyNumber } from '../api/portal/database.api';
import { logConsole, logTransaction } from '../common/log';
import { handleThunkError } from '../common/util';
import { LogType } from '../models/log.model';
import { IDatabase } from '../models/portal/database.model';
import { ConfigName, updateConfigObject } from '../sqlite/common/config';
import { setIsLoadingCountState } from './appSlice';
import { RootState } from '.';

interface IDatabaseState {
  databasesSearch: IDatabase[];
  databasesSearchLoading: boolean;
  selectedDatabase: IDatabase | undefined;
}

const initialState: IDatabaseState = {
  databasesSearch: [],
  databasesSearchLoading: false,
  selectedDatabase: undefined,
};

/// THUNKS

export const fetchDatabasesByCompanyNumberAsync = createAsyncThunk<{}, number | string>('database/fetchDatabasesByCompanyNumberAsync', async (companyNumber, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Fetch databases by company number '${companyNumber}'`, '[database] fetchDatabasesByCompanyNumberAsync');
  try {
    dispatch(setIsLoadingCountState(1));
    dispatch(setDatabasesSearchLoadingState(true));

    const responseData = await getDatabasesByCompanyNumber(companyNumber.toString());

    if (responseData.length === 0) {
      const { selectedDatabase } = (getState() as RootState).database;
      selectedDatabase && responseData.push(selectedDatabase);
    }
    dispatch(setDatabasesSearchState(responseData));

    dispatch(setIsLoadingCountState(-1));
    dispatch(setDatabasesSearchLoadingState(false));
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1), setDatabasesSearchLoadingState(false));
  }
});

export const setSelectedDatabaseStoreAsync = createAsyncThunk<{}, IDatabase>('database/setSelectedDatabaseStoreAsync', async (database, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Set selected database with id '${database.dbId}' (${database.dbDisplayName}) to store`, '[database] setSelectedDatabaseStoreAsync');
  try {
    dispatch(setSelectedDatabaseState(database));
    await updateConfigObject<IDatabase>(ConfigName.SelectedDatabase, database, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

/// SLICE

export const databaseSlice = createSlice({
  name: 'database',
  initialState,
  reducers: {
    setDatabasesSearchState: (state, action: PayloadAction<IDatabase[]>) => {
      logConsole(LogType.Info, '[database] setDatabasesSearchState');
      state.databasesSearch = action.payload;
    },
    setDatabasesSearchLoadingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, '[database] setDatabasesSearchLoadingState');
      state.databasesSearchLoading = action.payload;
    },
    setSelectedDatabaseState: (state, action: PayloadAction<IDatabase>) => {
      logConsole(LogType.Info, '[database] setSelectedDatabaseState');
      state.selectedDatabase = action.payload;
    },
  },
});

/// REDUCERS

export const { setDatabasesSearchState, setDatabasesSearchLoadingState, setSelectedDatabaseState } = databaseSlice.actions;

/// SELECTORS

// export const selectDatabasesSearchState = (state: RootState) => state.database.databasesSearch;

export default databaseSlice.reducer;
